/**
 * Install-nudge budget.
 *
 * The install card used to be strictly one-and-done: a single dismissal hid it
 * for good. That is very quiet, but it also means one accidental tap on the ×
 * costs the install forever. This replaces it with a small, uniform budget —
 * ask at most {@link MAX_DISMISSALS} times, at least {@link COOLDOWN_DAYS}
 * apart, then never again.
 *
 * The cooldown is the important half. A pure count ("show it 3 times") burns
 * all three asks in a single evening of browsing and reads as a popup; spacing
 * them out is what keeps it an invitation.
 *
 * Deliberately uniform across platforms: iOS and Android get the same budget
 * and the same cadence. Only the *button* differs (native prompt vs. the manual
 * Add-to-Home-Screen guide), and that is decided by `useInstallPrompt`, not
 * here. No device-specific nudge logic.
 */

/** How many times we may ask before giving up permanently. */
export const MAX_DISMISSALS = 3
/** Minimum days between two asks. */
export const COOLDOWN_DAYS = 7

/**
 * Storage generation. Bumping this resets the budget for everyone, which is
 * only safe because the install card has never shipped to production — the
 * PWA work is unreleased, so no real user has spent an ask yet.
 */
export const NUDGE_KEY = 'cf:install-nudge-v2'

/**
 * The v1 one-and-done flag, written by the pre-budget card.
 *
 * v2 deliberately does NOT consult it. Honouring it would only ever stop the
 * handful of devices that tested the unreleased build — there is no production
 * cohort to protect — and in exchange it made the budget impossible to test on
 * a device that had already dismissed once. The key is actively cleaned up on
 * read so it does not linger in anyone's localStorage.
 */
export const LEGACY_DISMISSED_KEY = 'install-card-dismissed'

const DAY_MS = 24 * 60 * 60 * 1000

export interface NudgeState {
  /** How many times the user has dismissed the card. */
  dismissals: number
  /** ISO timestamp of the last time the card was actually shown. */
  lastShownAt: string | null
  /** Terminal: installed, or the dismissal budget is spent. Never ask again. */
  stopped: boolean
}

const fresh = (): NudgeState => ({ dismissals: 0, lastShownAt: null, stopped: false })

/**
 * Read persisted state. Never throws — private mode, disabled storage, corrupt
 * JSON and malformed shapes all degrade to a fresh state.
 */
export function readNudgeState(): NudgeState {
  if (typeof window === 'undefined') return fresh()
  try {
    // Sweep the retired v1 flag so it doesn't linger. Its value is deliberately
    // ignored — see LEGACY_DISMISSED_KEY.
    if (window.localStorage.getItem(LEGACY_DISMISSED_KEY) !== null) {
      window.localStorage.removeItem(LEGACY_DISMISSED_KEY)
    }
    const raw = window.localStorage.getItem(NUDGE_KEY)
    if (!raw) return fresh()
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return fresh()
    const o = parsed as Partial<NudgeState>
    return {
      dismissals: typeof o.dismissals === 'number' && o.dismissals >= 0 ? o.dismissals : 0,
      lastShownAt: typeof o.lastShownAt === 'string' ? o.lastShownAt : null,
      stopped: o.stopped === true,
    }
  } catch {
    return fresh()
  }
}

/** Persist state. Never throws (quota / private mode). */
export function writeNudgeState(state: NudgeState): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(NUDGE_KEY, JSON.stringify(state))
  } catch {
    /* storage unavailable — the nudge just won't be remembered this session */
  }
}

/** Is the card allowed to appear right now? Availability is decided elsewhere. */
export function shouldShowNudge(state: NudgeState, now: Date = new Date()): boolean {
  if (state.stopped) return false
  if (state.dismissals >= MAX_DISMISSALS) return false
  if (!state.lastShownAt) return true
  const last = Date.parse(state.lastShownAt)
  if (Number.isNaN(last)) return true
  return now.getTime() - last >= COOLDOWN_DAYS * DAY_MS
}

/** Mark that the card was rendered — starts the cooldown. */
export function recordShown(state: NudgeState, now: Date = new Date()): NudgeState {
  return { ...state, lastShownAt: now.toISOString() }
}

/** Mark a dismissal, stopping permanently once the budget is spent. */
export function recordDismissed(state: NudgeState, now: Date = new Date()): NudgeState {
  const dismissals = state.dismissals + 1
  return {
    ...state,
    dismissals,
    lastShownAt: now.toISOString(),
    stopped: dismissals >= MAX_DISMISSALS,
  }
}

/**
 * Mark the app as installed. Persisting this matters: `display-mode:
 * standalone` is only true INSIDE the installed app, so the same device opening
 * a normal browser tab looks uninstalled and would otherwise be nagged again.
 */
export function recordInstalled(state: NudgeState): NudgeState {
  return { ...state, stopped: true }
}
